import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBusiness, useSetTaxProfile } from '../api/hooks';
import type { BusinessSummary, Company } from '../api/types';
import { cap, dt, MONTHS_L, money0, todayIso } from '../lib/format';
import { useUi } from '../state/ui';
import { Btn, Field, SelectField, SwitchRow } from './ui';

type Kind = 'month' | 'quarter' | 'year';

export const kindOf = (p: string): Kind =>
  /^\d{4}$/.test(p) ? 'year' : p.includes('Q') ? 'quarter' : 'month';

/** Lidský název období: „Říjen 2026", „4. čtvrtletí 2026", „Rok 2026". */
export function periodLabel(p: string): string {
  const y = p.slice(0, 4);
  const k = kindOf(p);
  if (k === 'year') return `Rok ${y}`;
  if (k === 'quarter') return `${p.slice(6)}. čtvrtletí ${y}`;
  return `${cap(MONTHS_L[Number(p.slice(5, 7)) - 1] ?? '')} ${y}`;
}

/** Posun období o jedno dopředu/dozadu. */
export function shiftPeriod(p: string, dir: 1 | -1): string {
  const y = Number(p.slice(0, 4));
  const k = kindOf(p);
  if (k === 'year') return String(y + dir);
  if (k === 'quarter') {
    const q = Number(p.slice(6)) + dir;
    if (q < 1) return `${y - 1}-Q4`;
    if (q > 4) return `${y + 1}-Q1`;
    return `${y}-Q${q}`;
  }
  const m = Number(p.slice(5, 7)) + dir;
  if (m < 1) return `${y - 1}-12`;
  if (m > 12) return `${y + 1}-01`;
  return `${y}-${String(m).padStart(2, '0')}`;
}

/** Převod mezi druhy období se zachováním roku a (u měsíce/čtvrtletí) polohy v roce. */
export function convertPeriod(p: string, to: Kind, today = todayIso()): string {
  const y = p.slice(0, 4);
  const from = kindOf(p);
  const month =
    from === 'month'
      ? Number(p.slice(5, 7))
      : from === 'quarter'
        ? (Number(p.slice(6)) - 1) * 3 + 1
        : y === today.slice(0, 4)
          ? Number(today.slice(5, 7))
          : 12;
  if (to === 'year') return y;
  if (to === 'quarter') return `${y}-Q${Math.ceil(month / 3)}`;
  return `${y}-${String(month).padStart(2, '0')}`;
}

const METHOD: Record<NonNullable<BusinessSummary['incomeTax']>['method'], string> = {
  corporate: 'daň právnické osoby',
  personal_actual: 'OSVČ, skutečné výdaje',
  personal_flat: 'OSVČ, výdajový paušál',
};

/**
 * Výsledky podnikání v Přehledu: filtr měsíc / čtvrtletí / rok; tržby, náklady, zisk,
 * DPH k odvodu (u § 90 jen z přirážky) a odhad daně z příjmů za rok.
 */
export function BusinessPanel({ company }: { company: Company }) {
  const companyId = company.id;
  const currency = company.currency ?? 'CZK';
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const period = params.get('obdobi') ?? today.slice(0, 7);
  const kind = kindOf(period);
  const { data, error, isFetching } = useBusiness(companyId, period);
  const { openSheet, closeSheet, toast } = useUi();
  const fmt = (v: string | number) => money0(v, currency);

  if (error && !data) return null;

  const setPeriod = (p: string) => {
    const next = new URLSearchParams(params);
    next.set('obdobi', p);
    setParams(next, { replace: true });
  };
  const next = shiftPeriod(period, 1);
  const future =
    next.slice(0, 4) > today.slice(0, 4) ||
    (kind !== 'year' && next > convertPeriod(today.slice(0, 7), kind));

  const editTax = () =>
    data &&
    openSheet(
      <TaxProfileSheet
        company={company}
        vatPayer={data.vatPayer}
        legalForm={data.legalForm}
        flatExpensePct={data.flatExpensePct}
        onDone={() => {
          closeSheet();
          toast('Nastavení daní uloženo');
        }}
        onCancel={closeSheet}
      />,
    );

  const profit = Number(data?.profit ?? 0);
  const payable = Number(data?.vat?.payable ?? 0);
  const tax = data?.incomeTax;

  return (
    <section className={`biz${isFetching ? ' busy' : ''}`} aria-label="Výsledky podnikání">
      <div className="biz-head">
        <div className="seg sm" role="tablist" aria-label="Druh období">
          {(
            [
              ['month', 'Měsíc'],
              ['quarter', 'Čtvrtletí'],
              ['year', 'Rok'],
            ] as [Kind, string][]
          ).map(([k, l]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              className={kind === k ? 'on' : ''}
              onClick={() => setPeriod(convertPeriod(period, k))}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="biz-nav">
          <button
            type="button"
            aria-label="Předchozí období"
            onClick={() => setPeriod(shiftPeriod(period, -1))}
          >
            ‹
          </button>
          <span className="biz-label">{periodLabel(period)}</span>
          <button
            type="button"
            aria-label="Další období"
            disabled={future}
            onClick={() => setPeriod(next)}
          >
            ›
          </button>
        </div>
      </div>

      <div className="biz-grid">
        <div className="biz-tile">
          <div className="k">Tržby bez DPH</div>
          <div className="v">{data ? fmt(data.revenue.net) : '…'}</div>
          {data && <div className="l2">prodáno {data.unitsSold} ks</div>}
        </div>
        <div className="biz-tile">
          <div className="k">Náklady</div>
          <div className="v">{data ? fmt(data.costs.total) : '…'}</div>
          {data && (
            <div className="l2">
              zboží {fmt(data.costs.goodsSold)} · ostatní {fmt(data.costs.other)}
            </div>
          )}
        </div>
        <div className="biz-tile">
          <div className="k">Zisk</div>
          <div className={`v ${profit < 0 ? 'bad' : 'ok'}`}>{data ? fmt(data.profit) : '…'}</div>
          {data && <div className="l2">marže {data.marginPct} %</div>}
        </div>
        <div className="biz-tile">
          {data?.vat ? (
            <>
              <div className="k">{payable < 0 ? 'Nadměrný odpočet DPH' : 'DPH k odvodu'}</div>
              <div className="v">{fmt(Math.abs(payable))}</div>
              <div className="l2">
                {Number(data.vat.marginVat) > 0
                  ? `z toho § 90 z přirážky ${fmt(data.vat.marginVat)}`
                  : `výstup ${fmt(data.vat.output)} · odpočet ${fmt(data.vat.deduction)}`}
              </div>
            </>
          ) : (
            <>
              <div className="k">DPH</div>
              <div className="v">{data ? 'neplátce' : '…'}</div>
              {data && (
                <div className="l2">
                  <button type="button" className="link" onClick={editTax}>
                    Jsme plátci – nastavit
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="biz-tax">
        <div className="grow">
          <div className="k">
            Daň z příjmů {tax?.year ?? period.slice(0, 4)}
            {tax ? ` · ${METHOD[tax.method]} ${tax.rateLabel}` : ''}
          </div>
          {tax ? (
            <>
              <div className="v">
                {fmt(tax.tax)} <span className="muted">zatím, do {dt(tax.to)}</span>
              </div>
              <div className="l2">
                <span>základ {fmt(tax.base)}</span>
                {tax.projected && (
                  <span>
                    výhled na celý rok ≈ <b>{fmt(tax.projected.tax)}</b> (při stejném tempu)
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="v muted">{data ? 'období ještě nezačalo' : '…'}</div>
          )}
        </div>
        {data && (
          <Btn sm onClick={editTax}>
            Nastavit daň
          </Btn>
        )}
      </div>
      {data && (
        <details className="biz-notes">
          <summary>Jak se to počítá</summary>
          {[...data.notes, ...(tax?.notes ?? [])].map((n) => (
            <p key={n}>{n}</p>
          ))}
          <p>
            Zboží skladem: {fmt(data.inventory.value)} ({data.inventory.units} ks) – nákup na sklad
            v období {fmt(data.costs.stockPurchases)}.
          </p>
        </details>
      )}
    </section>
  );
}

function TaxProfileSheet({
  company,
  vatPayer,
  legalForm,
  flatExpensePct,
  onDone,
  onCancel,
}: {
  company: Company;
  vatPayer: boolean;
  legalForm: 'sro' | 'osvc';
  flatExpensePct: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const save = useSetTaxProfile();
  const [form, setForm] = useState(legalForm);
  const [pct, setPct] = useState(String(flatExpensePct));
  const [vat, setVat] = useState(vatPayer);
  const [dic, setDic] = useState(company.dic ?? (company.ico ? `CZ${company.ico}` : 'CZ'));
  const [vatPeriod, setVatPeriod] = useState<'month' | 'quarter'>(
    company.vatPeriod === 'quarter' ? 'quarter' : 'month',
  );
  const [from, setFrom] = useState(`${todayIso().slice(0, 4)}-01-01`);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        companyId: company.id,
        legalForm: form,
        flatExpensePct: form === 'osvc' ? Number(pct) : 0,
        vatPayer: vat,
        ...(vat
          ? {
              dic: dic.replace(/\s/g, '').toUpperCase(),
              vatPeriod,
              ...(vatPayer ? {} : { vatPayerFrom: from }),
            }
          : {}),
      },
      { onSuccess: onDone },
    );
  };
  return (
    <form onSubmit={submit}>
      <h3>DPH a daň z příjmů</h3>
      <SwitchRow
        className="mb"
        title="Plátce DPH"
        subtitle={vat ? 'doklady s DPH, přiznání a kontrolní hlášení' : 'doklady bez DPH'}
        checked={vat}
        onChange={setVat}
      />
      {vat && (
        <>
          <div className="two">
            <Field
              id="tax-dic"
              label="DIČ"
              value={dic}
              onChange={(e) => setDic(e.target.value)}
              required
            />
            <SelectField
              id="tax-period"
              label="Zdaňovací období"
              value={vatPeriod}
              onChange={(e) => setVatPeriod(e.target.value as 'month' | 'quarter')}
            >
              <option value="month">měsíční</option>
              <option value="quarter">čtvrtletní</option>
            </SelectField>
          </div>
          {!vatPayer && (
            <Field
              id="tax-from"
              label="Plátcem DPH od"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              required
            />
          )}
        </>
      )}
      <h3 className="mt">Odhad daně z příjmů</h3>
      <p className="muted mb">
        Podle právní formy se počítá daň právnické osoby (21 %), nebo daň OSVČ (15 % / 23 % po slevě
        na poplatníka).
      </p>
      <SelectField
        id="tax-form"
        label="Právní forma"
        value={form}
        onChange={(e) => setForm(e.target.value as 'sro' | 'osvc')}
      >
        <option value="sro">s.r.o. / a.s. (právnická osoba)</option>
        <option value="osvc">OSVČ (fyzická osoba)</option>
      </SelectField>
      {form === 'osvc' && (
        <SelectField
          id="tax-flat"
          label="Výdaje"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
        >
          <option value="0">Skutečné výdaje (přijaté faktury)</option>
          <option value="60">Paušál 60 % – obchod, volná živnost</option>
          <option value="80">Paušál 80 % – řemeslná živnost</option>
          <option value="40">Paušál 40 % – jiná samostatná činnost</option>
          <option value="30">Paušál 30 % – nájem</option>
        </SelectField>
      )}
      {save.error && <p className="form-error">{save.error.message}</p>}
      <Btn type="submit" variant="primary" full loading={save.isPending}>
        Uložit
      </Btn>
      <Btn full onClick={onCancel}>
        Zrušit
      </Btn>
    </form>
  );
}
